import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BankIdBankInterface, ProviderInterface } from '@app/interfaces/bankid-bank.interface';
import { BankIdBanksService } from '@app/services/bankid-banks.service';
import { BackendService } from '@app/services/backend.service';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BankIdStatusEnum } from '@app/enums/bankid-status.enum';
import { SnackbarService } from '@app/services/snackbar.service';
import { ContactsService } from '@app/services/contacts.service';
import { BankIdVerifiedByEnum } from '@app/enums/bankid-verified-by.enum';
import { ContactInterface } from '@app/interfaces/contact.interface';
import { Subject } from 'rxjs';
import { MainService } from '@app/services/main.service';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SlicePipe } from '@angular/common';
import { HypoRadioComponent } from '@app/components/form-components/hypo-radio/hypo-radio.component';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

@Component({
    selector: 'app-bankid-dialog',
    templateUrl: './bankid-dialog.component.html',
    styleUrls: ['./bankid-dialog.component.scss'],
    imports: [
        MatDialogModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        SlicePipe,
        HypoRadioComponent,
    ],
})
export class BankIdDialogComponent implements OnInit, OnDestroy {
    matDialogRef = inject<MatDialogRef<BankIdDialogComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA);

    protected readonly bankIdBankService = inject(BankIdBanksService);
    protected readonly backendService = inject(BackendService);
    protected readonly fb = inject(UntypedFormBuilder);
    protected readonly snackbarService = inject(SnackbarService);
    protected readonly contactService = inject(ContactsService);
    protected readonly mainService = inject(MainService);

    protected destroyed$: Subject<void> = new Subject<void>();

    BankIdStatusEnum: typeof BankIdStatusEnum = BankIdStatusEnum;
    BankIdVerifiedByEnum: typeof BankIdVerifiedByEnum = BankIdVerifiedByEnum;
    public applicant: ContactInterface;
    public bankIdBanks: ProviderInterface[] = [];
    form: UntypedFormGroup;
    public isLoading: boolean = true;
    public mortgage: MortgageInterface;

    public close(): void {
        this.matDialogRef.close();
    }

    public ngOnInit() {
        this.applicant = this.data.applicant;

        this.mortgage = this.data.mortgage;
        let verifiedBy = BankIdVerifiedByEnum.bankid;
        if (this.applicant.verifiedStatus?.bankId === BankIdStatusEnum.verified) {
            verifiedBy = BankIdVerifiedByEnum.bankid;
        }
        if (this.applicant.verifiedStatus?.courier === BankIdStatusEnum.verified) {
            verifiedBy = BankIdVerifiedByEnum.courier;
        }

        this.form = this.fb.group({
            verifiedBy: [
                {
                    value: verifiedBy,
                    disabled: this.isVerified(),
                },
                Validators.required,
            ],
        });

        let contactId = this.applicant?._id;
        if (this.isVerified()) {
            contactId = 'asd123yxc'; // provide fakeId when already verified
        }
        // @API bankid/banks.find
        this.bankIdBankService
            .find({ query: { contactId } })
            .then((banks: ProviderInterface[]) => {
                if (banks?.length) {
                    this.bankIdBanks = banks;
                }
            })
            .catch(() => {
                this.snackbarService.showError(
                    'Bohužel se nám nepodařilo načíst seznam bank. Prosíme, zkuste to později nebo nás kontaktujte.'
                );
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    ngOnDestroy(): void {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    public isVerified(): boolean {
        return Object.values(this.applicant.verifiedStatus ?? {}).some((a) => a === BankIdStatusEnum.verified);
    }

    public authorizeByBankId(bank: BankIdBankInterface): void {
        if (this.isVerified()) {
            this.snackbarService.showError('Identita již byla v pořádku ověřena...');
            return;
        }
        if (this.backendService.lsIsAvailable) {
            localStorage.setItem('latestMortgageId', this.data.offer._id);
        }
        if (this.mainService.isDevelopment) {
            console.log(`BankID info ${JSON.stringify(bank)}`);
            console.log(`BankID redirect ${bank.authorizationURI}`);
        }
        window.open(bank.authorizationURI, '_blank');
        this.close();
    }

    public save(): void {
        const data = this.form.value;
        if (this.form.valid && data.verifiedBy && !this.isVerified()) {
            const contactPatch = {};
            if (data.verifiedBy === BankIdVerifiedByEnum.bankid) {
                contactPatch['verifiedStatus.bankId'] = BankIdStatusEnum.waiting;
            }
            if (data.verifiedBy === BankIdVerifiedByEnum.courier) {
                contactPatch['verifiedStatus.courier'] = BankIdStatusEnum.waiting;
            }
            this.contactService
                .patch(this.applicant._id, contactPatch)
                .then(() => {
                    this.close();
                })
                .catch((e) => {
                    console.error(e);
                    this.snackbarService.showError(
                        'Něco se pokazilo. Prosíme, kontaktujte nás telefonicky nebo e-mailem a my vše napravíme.'
                    );
                });
        } else {
            this.close();
        }
    }
}
